
import Tooltip from "@mui/material/Tooltip";
import { AnimatePresence, motion } from "framer-motion";
import Lottie from "lottie-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { List, RowComponentProps, useDynamicRowHeight } from "react-window";
import Empty from "../../../../assets/lotties/empty.json";
import LoadingComponent from "../../../../components/LoadingComponent/LoadingComponent.tsx";
import { colunasEntrega } from "../../../../components/TabelaV2/components/TableBodyDesordenado.js";
import { useProfile } from "../../../../contexts/ProfileContext.tsx";
import { useTabela } from "../../../../contexts/Tabela";
import { Embarque } from "../../../../models/Embarque.ts";
import "../styles/TabelaEmbarqueVirtualizada.css";
import { calcularLargurasColunas, defaultRowHeight, DOUBLE_CLICK_DELAY, ExtraEntregaRowProps, ExtraRowProps, getCellClasses, renderCellContent, renderEntregaCellContent } from "./helpers/TabelaEmbarqueVirtualizadaHelpers.tsx";

const MIN_TABLE_HEIGHT = 400;

type TabelaEmbarqueVirtualizadaProps = {
    dados: Embarque[];
    buscarOrdenacao: (ordem: string) => void;
    isLoading: boolean;
    registerRecalc?: (fn: () => void) => void;
    hasFooter?: boolean;
};

const TabelaEmbarqueVirtualizada = ({
    dados,
    buscarOrdenacao,
    isLoading,
    registerRecalc,
    hasFooter = true
}: TabelaEmbarqueVirtualizadaProps) => {
    const { contextoEmbarque, tabelaUsuario } = useProfile();
    const { tabelaRedux, setTabelaRedux } = useTabela();
    const navigate = useNavigate();
    const location = useLocation();

    const itemCount = useMemo(() => dados?.length ?? 0, [dados?.length]);

    const targetContentKey = useMemo(() => {
        if (isLoading) return "loading";
        if (itemCount === 0) return "empty";
        return "loaded";
    }, [isLoading, itemCount]);

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const clickTimeoutRef = useRef<number | null>(null);
    const lastRowKeyRef = useRef<string | number | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const [dadosEntrega, setDadosEntrega] = useState<Record<number, any>>({});
    const [linhaSelecionada, setLinhaSelecionada] = useState<number[]>([]);
    const [tabelaHeight, setTabelaHeight] = useState(600);
    const [contentKey, setContentKey] = useState<string>(targetContentKey);

    const showDropDowEmbarques = (row: Embarque) => {
        setDadosEntrega((prevState) => ({
            ...prevState,
            [row.idEmbarque]: row.entrega,
        }));
        if (linhaSelecionada.includes(row.idEmbarque)) {
            setLinhaSelecionada(
                linhaSelecionada.filter((id) => id !== row.idEmbarque)
            );
        } else {
            setLinhaSelecionada([...linhaSelecionada, row.idEmbarque]);
        }
    };

    const listHeight = useMemo(() => {
        const calculatedHeight = itemCount * defaultRowHeight;
        return Math.min(calculatedHeight, tabelaHeight);
    }, [itemCount, tabelaHeight]);

    const visibleColumns = useMemo(
        () => {
            return tabelaUsuario.Embarque
                .filter((c: any) => c.visivel === "true")
                .filter((c: any) => {
                    if (contextoEmbarque !== "templateEmbarque" && c.field === "descricao") {
                        return false;
                    }
                    return true;
                });
        },
        [tabelaUsuario.Embarque, contextoEmbarque]
    );

    const configDataHora = useMemo(() => {
        return tabelaUsuario["Embarque"]?.[50]?.configDataHora;
    }, [tabelaUsuario]);


    const configEntrega = useMemo(() => {
        return tabelaUsuario["Embarque"]?.[51]?.configEntrega;
    }, [tabelaUsuario]);

    const columnWidths = useMemo(
        () => calcularLargurasColunas(dados, visibleColumns, configDataHora),
        [dados, visibleColumns, configDataHora]
    );

    const totalGridWidth = useMemo(
        () => columnWidths.reduce((sum, w) => sum + w, 0),
        [columnWidths]
    );

    const gridTemplateColumns = useMemo(
        () => columnWidths.map((w) => `${w}px`).join(" "),
        [columnWidths]
    );

    const baseRowHeight = useDynamicRowHeight({
        defaultRowHeight,
    });

    const entregasColumnWidths = useMemo(
        () => {
            const todasEntregas = Object.values(dadosEntrega).flat() as any[];
            if (!todasEntregas.length) return colunasEntrega.map(() => 120);

            return calcularLargurasColunas(todasEntregas, colunasEntrega as any[], false);
        },
        [dadosEntrega]
    );

    const entregasTotalGridWidth = useMemo(
        () => entregasColumnWidths.reduce((sum, w) => sum + w, 0),
        [entregasColumnWidths]
    );

    const entregasGridTemplateColumns = useMemo(
        () => entregasColumnWidths.map((w) => `${w}px`).join(" "),
        [entregasColumnWidths]
    );

    const rowHeight = useCallback(
        (index: number) => {
            const base = defaultRowHeight;
            const row = dados[index];
            if (!row) return base;

            const isExpanded = linhaSelecionada.includes(row.idEmbarque);
            if (!isExpanded) return base;

            const entregas = dadosEntrega[row.idEmbarque] ?? [];
            if (!entregas.length) return base;


            const headerHeight = 24;
            const entregaRowHeight = 24;
            const maxVisibleRows = 8;
            const visibleRows = Math.min(entregas.length, maxVisibleRows);

            return base + headerHeight + entregaRowHeight * visibleRows;
        },
        [dados, linhaSelecionada, dadosEntrega]
    );

    const handleSingleClick = useCallback(
        (row: any) => {
            setTabelaRedux({
                colunaSelecionada: row["idEmbarque"],
                usuarioAlteracao: row.usuario,
                dataAlteracao: row.data,
                row,
            });
        },
        [setTabelaRedux]
    );

    const handleDoubleClick = useCallback(
        (row: any) => {
            if (location.pathname.startsWith("/viagens/") && contextoEmbarque !== "todos") {
                navigate(`/viagens/${contextoEmbarque}/cadastro/${row.idEmbarque}`);
            }
        },
        [navigate, contextoEmbarque, location.pathname]
    );

    const handleRowClick = useCallback(
        (row: Embarque) => {

            const rowKey =
                (row as any)["idEmbarque"] ?? (row as any).idEmbarque;


            if (clickTimeoutRef.current && lastRowKeyRef.current === rowKey) {
                window.clearTimeout(clickTimeoutRef.current);
                clickTimeoutRef.current = null;
                lastRowKeyRef.current = null;

                handleDoubleClick(row);
                return;
            }


            lastRowKeyRef.current = rowKey;

            clickTimeoutRef.current = window.setTimeout(() => {
                handleSingleClick(row);
                clickTimeoutRef.current = null;
                lastRowKeyRef.current = null;
            }, DOUBLE_CLICK_DELAY);
        },
        [handleSingleClick, handleDoubleClick]
    );

    useLayoutEffect(() => {
        if (wrapperRef.current && !isLoading) {
            const rect = wrapperRef.current.getBoundingClientRect();
            const footerReserva = 140;
            const disponivel = window.innerHeight - rect.top - footerReserva;
            setTabelaHeight(disponivel);
        }
    }, [isLoading]);

    useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        timeoutRef.current = setTimeout(
            () => setContentKey(targetContentKey),
            250
        );
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [targetContentKey]);

    const recalcHeight = useCallback(() => {
        const el = wrapperRef.current;
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const next = window.innerHeight - rect.top - (hasFooter ? 140 : 70);

        setTabelaHeight(Math.max(MIN_TABLE_HEIGHT, next));
    }, []);

    useEffect(() => {
        const handleResize = () => recalcHeight();
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [recalcHeight]);

    useEffect(() => {
        registerRecalc?.(recalcHeight);
    }, [registerRecalc, recalcHeight]);

    const HeaderRow = () => {
        return (
            <div
                className="tabela-virtualizada-header"
                style={{
                    display: "grid",
                    gridTemplateColumns,
                    width: totalGridWidth,
                }}
            >
                {visibleColumns.map((coluna: any, index: number) => {
                    const label =
                        coluna.nomeColuna ||
                        coluna.label ||
                        coluna.nome ||
                        coluna.header ||
                        coluna.field ||
                        coluna.campo;

                    return (
                        <Tooltip key={String(label) + index} title={label}>
                            <div
                                key={String(label) + index}
                                className={
                                    "tabela-virtualizada-header-cell"
                                }
                                onClick={() =>
                                    buscarOrdenacao &&
                                    buscarOrdenacao(coluna.field || coluna.campo || coluna.nome)
                                }
                            >
                                {label}
                            </div>
                        </Tooltip>
                    );
                })}
            </div>
        );
    };

    const EntregaRow = ({
        index,
        style,
        dados,
        gridTemplateColumns,
        columnWidths,
    }: RowComponentProps<ExtraEntregaRowProps>) => {
        const row = dados[index];
        const entrega = dados[index];

        const isSelected =
            tabelaRedux?.colunaSelecionada === (row as any)["idEntrega"];
        const isStriped = index % 2 === 0;

        const rowClasses = [
            "linha-entrega linha-entrega",
            isStriped ? "linha-tabela--striped" : "",
            isSelected ? "linha-tabela--selecionada" : "",
        ]
            .filter(Boolean)
            .join(" ");

        return (
            <div
                className={rowClasses}
                style={{
                    ...style,
                    display: "grid",
                    gridTemplateColumns,
                    width: columnWidths.reduce((s, w) => s + w, 0),
                }}
                onClick={() => {
                    setTabelaRedux({
                        colunaSelecionada: row["idEntrega"],
                        usuarioAlteracao: row.usuario,
                        dataAlteracao: row.data,
                        row,
                    });
                }}
            >
                {colunasEntrega.map((coluna: any, colIndex: number) => {
                    const key = (coluna?.campo || coluna?.field || coluna?.nome) as string;
                    const content = renderEntregaCellContent(coluna, entrega);
                    let className = "tabela-virtualizada-cell";
                    if (["classificacao", "freteMotorista", "freteFiscal", "icms", "pedagio"].includes(coluna.field)) {
                        className += " td-right";
                    }
                    return (
                        <div
                            key={String(key) + index}
                            className={className}
                            style={{ width: columnWidths[colIndex] }}
                        >
                            {content}
                        </div>
                    );
                })}
            </div>
        );
    };

    const Row = ({
        index,
        style,
        dados,
        visibleColumns,
        gridTemplateColumns,
    }: RowComponentProps<ExtraRowProps>) => {
        const row = dados[index];
        const isSelected =
            tabelaRedux?.colunaSelecionada === (row as any)["idEmbarque"];
        const isStriped = index % 2 === 0;

        const hasCte =
            row.cte &&
            row.cte !== " " &&
            row.cte !== "  ";

        const rowClasses = [
            "linha-tabela",
            "hover",
            isStriped ? "linha-tabela--striped" : "",
            isSelected ? "linha-tabela--selecionada" : "",
            hasCte ? "cte" : "",
            row.cancelado ? "cancelado" : "",
            row.foiCarregado === false ? "cancelado" : "",
            row.foiCarregado === true ? "cte" : "",
            row.foiCarregado === null ? "null" : "",
            row.placaCavalo === null ? "negrito-embarque" : "",
        ]
            .filter(Boolean)
            .join(" ");

        const expandida = linhaSelecionada.includes(row.idEmbarque);

        return (
            <div
                style={{
                    ...style,
                    width: totalGridWidth,
                }}
            >
                <div
                    className={rowClasses}
                    style={{
                        display: "grid",
                        height: baseRowHeight.getAverageRowHeight(),
                        gridTemplateColumns,
                        width: totalGridWidth,
                    }}
                    onClick={() => handleRowClick(row)}
                >
                    {visibleColumns.map((coluna: any, colIndex: number) => {
                        const key =
                            (coluna?.campo || coluna?.field || coluna?.nome) as keyof Embarque;
                        const content = renderCellContent(coluna, row, {
                            configEntrega,
                            configDataHora,
                            linhaSelecionada,
                            showDropDowEmbarques,
                        });
                        const today = new Date();
                        const algumaDataVencida =
                            (row.dataValidadeAntt &&
                                new Date(row.dataValidadeAntt) < today) ||
                            (row.dataValidadeCnhMotorista &&
                                new Date(row.dataValidadeCnhMotorista) < today) ||
                            (row.dataValidadeComprovanteResidencia &&
                                new Date(row.dataValidadeComprovanteResidencia) < today) ||
                            (row.dataValidadeCrlv &&
                                new Date(row.dataValidadeCrlv) < today) ||
                            (row.dataValidadeFotoVeiculo &&
                                new Date(row.dataValidadeFotoVeiculo) < today);
                        const cellClasses = getCellClasses(coluna, row, algumaDataVencida ?? false);
                        return (
                            <div
                                key={String(key)}
                                className={cellClasses}
                                style={{ width: columnWidths[colIndex] }}
                            >
                                {content}
                            </div>
                        );
                    })}
                </div>
                {expandida && dadosEntrega[row.idEmbarque] && (
                    <div
                        className="linha-entregas-wrapper"
                        style={{
                            width: totalGridWidth,
                            padding: "2px 0 2px 32px",
                            display: "flex",
                            flexDirection: "column",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            className="tabela-entregas-header"
                            style={{
                                display: "grid",
                                gridTemplateColumns: entregasGridTemplateColumns,
                                width: entregasTotalGridWidth,
                            }}
                        >
                            {colunasEntrega.map((coluna: any, index: number) => {
                                const label =
                                    coluna.nomeColuna ||
                                    coluna.label ||
                                    coluna.nome ||
                                    coluna.header ||
                                    coluna.field ||
                                    coluna.campo;
                                return (
                                    <Tooltip key={String(label) + index} title={label}>
                                        <div
                                            key={String(label) + index}
                                            className="tabela-virtualizada-header-cell"
                                        >
                                            {label}
                                        </div>
                                    </Tooltip>
                                );
                            })}
                        </div>
                        <List
                            style={{
                                width: entregasTotalGridWidth,
                                maxHeight: 8 * 24,
                                overflowX: "hidden",
                                overflowY: "auto",
                            }}
                            rowComponent={EntregaRow}
                            rowCount={dadosEntrega[row.idEmbarque].length}
                            rowHeight={24}
                            rowProps={{
                                dados: dadosEntrega[row.idEmbarque],
                                gridTemplateColumns: entregasGridTemplateColumns,
                                columnWidths: entregasColumnWidths,
                            }}
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <motion.div key={location.pathname} className="table-generica-container" ref={wrapperRef}>
            <AnimatePresence mode="wait">
                {contentKey === "empty" && (
                    <motion.div
                        key="empty"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="modal-encontrar-veiculo-initial-container"
                    >
                        <span>Nenhum item foi localizado.</span>
                        <Lottie animationData={Empty} loop autoplay style={{ height: 300 }} />
                    </motion.div>
                )}

                {contentKey === "loading" && <LoadingComponent key="loading" />}

                {contentKey === "loaded" && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="outer-wrapper-generica" key="tabela-embarque-loaded">
                        <div className="table-generica" id="tableWrapper">
                            <div className="wrapper-table-generica">
                                <HeaderRow />
                                <List
                                    style={{
                                        width: totalGridWidth,
                                        height: listHeight,
                                        overflowX: "hidden",
                                        overflowY: "auto",
                                    }}
                                    overscanCount={25}
                                    rowComponent={Row}
                                    rowCount={itemCount}
                                    rowHeight={rowHeight}
                                    rowProps={{ dados, visibleColumns, gridTemplateColumns }}
                                />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default TabelaEmbarqueVirtualizada;
